// Set-password / accept-invite page. Reached from the branded invite email via
// /accept-invite?token=<signed-invite-jwt>. It validates the token against the
// backend (GET /auth/invite/validate) to show who the invite is for, then lets
// the user set their password (POST /auth/invite/accept) — which sets it in
// Logto and marks their email verified. No session is required; this page is
// intercepted before the operator auth gate (see App.tsx).

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IOSentraLogo } from '@/components/IOSentraLogo';
import { IOSentraWordmark } from '@/components/IOSentraWordmark';

const API_BASE = import.meta.env.VITE_API_URL || '';

type Phase = 'validating' | 'invalid' | 'form' | 'submitting' | 'done';

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

// Same policy the backend enforces (min 8). Give live guidance so the user
// isn't surprised by a server rejection.
function pwIssues(pw: string): string | null {
  if (pw.length < 8) return 'min';
  return null;
}

export function AcceptInvitePage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [token] = useState(tokenFromUrl);
  const [phase, setPhase] = useState<Phase>('validating');
  const [email, setEmail] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate the token once on mount.
  useEffect(() => {
    let alive = true;
    if (!token) { setPhase('invalid'); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/auth/invite/validate?token=${encodeURIComponent(token)}`);
        if (!alive) return;
        if (!res.ok) { setPhase('invalid'); return; }
        const body = await res.json();
        const data = body?.data ?? body;
        setEmail(data?.email ?? null);
        setName(data?.name ?? null);
        setPhase('form');
      } catch {
        if (alive) setPhase('invalid');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (pwIssues(password)) { setError(t('acceptInvite.errPwShort', 'Password must be at least 8 characters.')); return; }
    if (password !== confirm) { setError(t('acceptInvite.errPwMismatch', "Passwords don't match.")); return; }
    setPhase('submitting');
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/invite/accept`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const code = body?.error ?? body?.code;
        // 410 = token invalid/expired (verifyInviteToken threw). Others are transient.
        setError(
          res.status === 410 || code === 'invite_invalid_or_expired'
            ? t('acceptInvite.errExpired', 'This invitation has expired or is invalid. Ask an administrator to resend it.')
            : t('acceptInvite.errGeneric', 'Could not set your password. Please try again.'),
        );
        setPhase('form');
        return;
      }
      setPhase('done');
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch {
      setError(t('acceptInvite.errGeneric', 'Could not set your password. Please try again.'));
      setPhase('form');
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface/80 p-8 shadow-2xl backdrop-blur">
        <div className="mb-6 flex items-center gap-3">
          <IOSentraLogo className="h-9 w-9" />
          <IOSentraWordmark className="h-5" />
        </div>

        {phase === 'validating' && (
          <p className="text-muted">{t('acceptInvite.validating', 'Checking your invitation…')}</p>
        )}

        {phase === 'invalid' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              <h1 className="text-lg font-semibold">{t('acceptInvite.invalidTitle', 'Invitation not valid')}</h1>
            </div>
            <p className="text-sm text-muted">
              {t('acceptInvite.invalidBody', 'This invitation link is invalid or has expired. Ask your administrator to send a new one.')}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-2 text-sm font-medium text-primary hover:underline"
            >
              {t('acceptInvite.goToLogin', 'Go to sign in')}
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              <h1 className="text-lg font-semibold">{t('acceptInvite.doneTitle', 'Password set')}</h1>
            </div>
            <p className="text-sm text-muted">
              {t('acceptInvite.doneBody', 'Your account is ready. Redirecting you to sign in…')}
            </p>
          </div>
        )}

        {(phase === 'form' || phase === 'submitting') && (
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <h1 className="text-xl font-semibold text-fg">
                {t('acceptInvite.title', 'Set your password')}
              </h1>
              <p className="mt-1 text-sm text-muted">
                {name
                  ? t('acceptInvite.subtitleNamed', { defaultValue: 'Welcome, {{name}}. Choose a password for {{email}}.', name, email })
                  : t('acceptInvite.subtitle', { defaultValue: 'Choose a password for {{email}}.', email })}
              </p>
            </div>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-fg">{t('acceptInvite.password', 'Password')}</span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  className="w-full rounded-lg border border-border bg-black/20 py-2.5 pl-9 pr-10 text-fg outline-none focus:border-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-fg"
                  aria-label={showPw ? t('common:hide', 'Hide') : t('common:show', 'Show')}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <span className="mt-1 block text-xs text-muted">{t('acceptInvite.pwHint', 'At least 8 characters.')}</span>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-fg">{t('acceptInvite.confirm', 'Confirm password')}</span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  className="w-full rounded-lg border border-white/10 bg-black/20 py-2.5 pl-9 pr-3 text-fg outline-none focus:border-brand"
                  placeholder="••••••••"
                />
              </div>
            </label>

            {error && (
              <p className="flex items-center gap-2 text-sm text-red-400" role="alert">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
              </p>
            )}

            <button
              type="submit"
              disabled={phase === 'submitting'}
              className="tk-btn-primary w-full py-2.5 text-base disabled:opacity-60"
            >
              {phase === 'submitting' ? t('acceptInvite.submitting', 'Setting password…') : t('acceptInvite.submit', 'Set password & continue')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
